import { Typography } from '@mui/material'
import React from 'react'
import { z } from 'zod'

import { Form } from '@/app/component/form'
import { ActionsContainer, StartAdornment } from '@/app/component/form/common'
import SubmitButton from '@/app/component/form/submit-button'
import { MAX_AMOUNT, MAX_THRESHOLD, MIN_AMOUNT, MIN_THRESHOLD } from '@/app/module/payments/definitions'
import { useUpdateAutoRecharge } from '@/app/module/payments/hooks/use-update-auto-recharge'
import { Balance } from '@/app/types'

type Props = {
  autoRecharge: Balance
  onClose: () => void
}

const AutoRecharge: React.FC<Props> = ({ autoRecharge: initAutoRecharge, onClose }) => {
  const [updateAutoRecharge, { isLoading }] = useUpdateAutoRecharge()

  const defaultValues = React.useMemo(
    () => ({
      autoRecharge: initAutoRecharge?.autoRecharge ?? false,
      autoRechargeAmount: initAutoRecharge?.autoRechargeAmount ?? MIN_AMOUNT,
      autoRechargeThreshold: initAutoRecharge?.autoRechargeThreshold ?? MIN_THRESHOLD,
    }),
    [initAutoRecharge],
  )

  const fields = React.useMemo(
    () =>
      ({
        autoRecharge: {
          type: 'switch',
          fieldProps: {
            name: 'autoRecharge',
            label: 'Auto Recharge',
          },
        },
        autoRechargeAmount: {
          type: 'number',
          fieldProps: {
            name: 'autoRechargeAmount',
            label: 'Recharge at least',
            placeholder: '20',
            startAdornment: <StartAdornment position="start">$</StartAdornment>,
            info: (
              <>
                Minimum Amount ${MIN_AMOUNT.toFixed(2)}
                <br />
                Maximum Amount ${MAX_AMOUNT.toFixed(2)}
              </>
            ),
            required: true,
          },
        },
        autoRechargeThreshold: {
          type: 'number',
          fieldProps: {
            name: 'autoRechargeThreshold',
            label: 'When balance falls below',
            placeholder: '10',
            startAdornment: <StartAdornment position="start">$</StartAdornment>,
            info: (
              <>
                Minimum Amount ${MIN_THRESHOLD.toFixed(2)}
                <br />
                Maximum Amount ${MAX_THRESHOLD.toFixed(2)}
              </>
            ),
            required: true,
          },
        },
      }) as const,
    [],
  )

  const handleSubmit = React.useCallback(
    async (values: AutoRechargeFormValues) => {
      const success = await updateAutoRecharge(values)
      if (success) {
        onClose()
      }
    },
    [onClose, updateAutoRecharge],
  )

  return (
    <>
      <Typography variant="subtitle1" color="textSecondary" gutterBottom>
        Set automatic recharge amounts for your organization balance
      </Typography>
      <Form
        defaultValues={defaultValues}
        fields={fields}
        id="auto-recharge-form"
        name="auto-recharge-form"
        schema={autoRechargeSchema}
        shouldReset
        onSubmit={handleSubmit}
        renderActions={({ isDirty }) => (
          <ActionsContainer>
            <SubmitButton isDirty={isDirty} isSubmitting={isLoading}>
              Save
            </SubmitButton>
          </ActionsContainer>
        )}
      />
    </>
  )
}

const autoRechargeSchema = z.object({
  autoRecharge: z.boolean(),
  autoRechargeAmount: z
    .number({ invalid_type_error: 'Must be a number', required_error: 'Must be a number' })
    .min(MIN_AMOUNT, `Must be at least ${MIN_AMOUNT}`)
    .max(MAX_AMOUNT, `Must be at most ${MAX_AMOUNT}`)
    .describe('Recharge Amount'),
  autoRechargeThreshold: z
    .number({ invalid_type_error: 'Must be a number', required_error: 'Must be a number' })
    .min(MIN_THRESHOLD, `Must be at least ${MIN_THRESHOLD}`)
    .max(MAX_THRESHOLD, `Must be at most ${MAX_THRESHOLD}`)
    .describe('Threshold Amount'),
})

type AutoRechargeFormValues = z.infer<typeof autoRechargeSchema>

export default AutoRecharge
